/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import "sanitize.css/sanitize.css";
import "./index.css";
// Initialize languages
import "react-toastify/dist/ReactToastify.css";
import "./locales/i18n";

import FontFaceObserver from "fontfaceobserver";
import React from "react";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import { App } from "./app";
import { configureAppStore } from "./store/configureStore";
import { ThemeProvider } from "./styles/theme/ThemeProvider";

import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider } from "@mui/material/styles";
import { createRoot } from "react-dom/client";

import reportWebVitals from "./reportWebVitals";

const notoSansHebrewObserver = new FontFaceObserver("Noto Sans Hebrew", {});
const monaSansObserver = new FontFaceObserver("Mona-Sans", {});


Promise.allSettled([notoSansHebrewObserver.load(), monaSansObserver.load()]).then(
  (results) => {
    const allLoaded = results.every((r) => r.status === "fulfilled");
    if (allLoaded) {
      document.body.classList.add("fontLoaded");
    }
  }
);

const store = configureAppStore();
const MOUNT_NODE = document.getElementById("root") as HTMLElement;

interface Props {
  Component: typeof App;
}
const ConnectedApp = ({ Component }: Props) => (
  <>
    <CssBaseline />
    <Provider store={store}>
      <ToastContainer
        position="bottom-left"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <ThemeProvider>
        <StyledEngineProvider injectFirst>
          <HelmetProvider>
            <React.StrictMode>
              <Component />
            </React.StrictMode>
          </HelmetProvider>
        </StyledEngineProvider>
      </ThemeProvider>
    </Provider>
  </>
);

const render = (Component: typeof App) => {
  const root = createRoot(MOUNT_NODE);
  root.render(<ConnectedApp Component={Component} />);
};

// @ts-ignore
if (module && module?.hot) {
  // Hot reloadable translation json files and app
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  // @ts-ignore
  module?.hot.accept(["./app", "./locales/i18n"], () => {
    const root = createRoot(MOUNT_NODE);
    root.unmount();
    const App = require("./app").App;
    render(App);
  });
}

render(App);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// serviceWorker.unregister();
reportWebVitals();
